/**
 * Gating-Logik für den (eingeschränkten) Admin-Bereich (F2, Task 6).
 *
 * KuSe-Admins (Funktionsrolle `kuse_admin`, kein `is_admin`) nutzen den BESTEHENDEN
 * Admin-Bereich in beschnittener Form (Entscheidung F) — statt eines eigenen
 * Screens. Diese reinen Funktionen entscheiden NUR, welche Tabs/Optionen ein User
 * zu sehen bekommt; die eigentliche Rechte-Entscheidung (darf er eine konkrete
 * Rolle vergeben/entziehen, ein konkretes Tag pflegen) bleibt in den shared
 * Prädikaten (`canAssignFunctionRole`, `canManageTagInScope` aus
 * `@projektverwaltung/shared`) — hier NICHT nachgebaut, nur konsumiert.
 *
 * Input überall: ein `PermissionUser` (SessionUser erfüllt den Shape strukturell,
 * siehe `lib/permissions.ts`). Output: Listen/Booleans — damit ohne DOM/Session
 * per Logik-Test (`*.test.ts`) prüfbar.
 */
import {
  type FunctionRole,
  type PermissionUser,
  SEGMENT_SCOPED_ROLES,
  type Segment,
  scopeSegmentsForRole,
} from '@projektverwaltung/shared';

export type AdminTab =
  | 'users'
  | 'tags'
  | 'phases'
  | 'salesforce'
  | 'flags'
  | 'notifications'
  | 'actions'
  | 'prompts'
  | 'news'
  | 'neue_nutzer';

const ALL_ADMIN_TABS: AdminTab[] = [
  'users',
  'tags',
  'phases',
  'salesforce',
  'flags',
  'notifications',
  'actions',
  'prompts',
  'news',
  'neue_nutzer',
];

/** Für KuSe-Admin ohne is_admin: NUR Benutzer/Rollen + Tags + Neue Nutzer (#116: Lesen, Rollen, Geprüft; Task-Brief §2). */
const KUSE_ADMIN_TABS: AdminTab[] = ['users', 'tags', 'neue_nutzer'];

/**
 * Rollen-Dropdown für volle Admins (is_admin) — die bisherige `FN_ROLES`-Liste aus admin.tsx,
 * ergänzt um `kuse_admin` (Final-Review C1): `canAssignFunctionRole` erlaubt `is_admin` die
 * Vergabe von `kuse_admin` ausdrücklich (`actor.is_admin → return true`, VOR der
 * `targetRole === 'kuse_admin' → false`-Sperre, die nur für Nicht-Admin-Actor greift). Die
 * Sperre gilt ausschließlich für KuSe-Admin-Actor — siehe `KUSE_ADMIN_ASSIGNABLE_ROLES`
 * unten, die bewusst OHNE `kuse_admin` bleibt.
 */
export const FULL_ADMIN_ASSIGNABLE_ROLES: FunctionRole[] = [
  'projektleiter',
  'bereichsleiter',
  'kaufm_leitung',
  'mtb',
  'service_leitung',
  'manager',
  'kuse_admin',
];

/**
 * Rollen-Dropdown für KuSe-Admin ohne is_admin: `SEGMENT_SCOPED_ROLES` minus
 * `kuse_admin` selbst (Task-Brief §3) — genau die Rollen, die
 * `canAssignFunctionRole` einem KuSe-Admin im eigenen Segment ohnehin erlauben
 * würde.
 */
const KUSE_ADMIN_ASSIGNABLE_ROLES: FunctionRole[] = (
  SEGMENT_SCOPED_ROLES as readonly FunctionRole[]
).filter((role) => role !== 'kuse_admin');

/**
 * Fallback-Actor, solange die Session noch lädt (`useSession().user === null`).
 * Bildet das bisherige Verhalten nach: `admin.tsx` war vor Task 6 überhaupt
 * nicht an die Session gekoppelt (Sichtbarkeit kam ausschließlich aus der
 * Sidebar-Gate), zeigte also unconditional den vollen Bereich. Damit das
 * kurze Session-Hydrations-Fenster keine neue Einschränkung einführt, gilt in
 * dem Moment „wie voller Admin" — das Backend erzwingt die echten Rechte
 * ohnehin unabhängig davon.
 */
export const ADMIN_LOADING_FALLBACK_ACTOR: PermissionUser = {
  user_id: '',
  function_roles: [],
  is_admin: true,
};

/** Ist der User NUR über die Funktionsrolle `kuse_admin` im Admin-Bereich (kein globales `is_admin`)? */
export function isKuseAdminOnly(user: PermissionUser): boolean {
  if (user.is_admin) return false;
  return user.function_roles.some((fr) => fr.role === 'kuse_admin');
}

/** Sichtbarkeit des Sidebar-Admin-Eintrags (`sidebar.tsx`): `is_admin || hasRole('kuse_admin')`. */
export function canSeeAdminArea(user: PermissionUser): boolean {
  return user.is_admin || user.function_roles.some((fr) => fr.role === 'kuse_admin');
}

/**
 * Sichtbare Tabs im Admin-Bereich für den gegebenen User (Task-Brief §2).
 *
 * Fail-closed (Final-Review I3): vorher fiel jeder Nicht-„kuse-admin-only"-User in den
 * ALL_ADMIN_TABS-Zweig — auch ein Betrachter ohne jede Rolle, für den `canSeeAdminArea`
 * bereits `false` liefert und der die Seite regulär nie zu Gesicht bekommt (Sidebar blendet
 * sie aus, App.tsx-Routen-Guard leitet um). Diese Funktion sollte trotzdem für sich allein
 * das Richtige tun, statt sich blind auf die Verdrahtung an den Aufrufstellen zu verlassen.
 */
export function visibleAdminTabs(user: PermissionUser): AdminTab[] {
  if (!canSeeAdminArea(user)) return [];
  return isKuseAdminOnly(user) ? KUSE_ADMIN_TABS : ALL_ADMIN_TABS;
}

/** Vorausgewählter Tab beim Öffnen des Admin-Bereichs. */
export function defaultAdminTab(user: PermissionUser): AdminTab {
  return visibleAdminTabs(user)[0] ?? 'users';
}

/** Rollen im „Rolle zuweisen"-Dropdown einer User-Zeile (Task-Brief §3). */
export function assignableFunctionRoles(user: PermissionUser): FunctionRole[] {
  return isKuseAdminOnly(user) ? KUSE_ADMIN_ASSIGNABLE_ROLES : FULL_ADMIN_ASSIGNABLE_ROLES;
}

/**
 * Segmente, auf die die Segment-Auswahl im „Rolle zuweisen"-Formular beschränkt
 * ist. `null` = keine Einschränkung (voller Admin, alle Segmente wählbar). Für
 * einen KuSe-Admin die eigenen `kuse_admin`-Segmente — bei genau einem Segment
 * kann der Aufrufer das als „fest vorbelegt/disabled" behandeln (Task-Brief §3).
 */
export function assignableSegments(user: PermissionUser): Segment[] | null {
  return isKuseAdminOnly(user) ? scopeSegmentsForRole(user, 'kuse_admin') : null;
}

/**
 * Darf der User User-STAMMDATEN verwalten (anlegen, `is_admin`-Toggle,
 * löschen)? Diese drei Endpunkte (`POST/PATCH/DELETE /v1/admin/users`) sind im
 * Backend strikt `is_admin`-only (`requireAdmin()`, siehe `routes/admin.ts`) —
 * ein KuSe-Admin bekäme dort immer 403. Deshalb hier ausgeblendet statt einen
 * Klick anzubieten, der serverseitig sicher scheitert (Task-Brief §3: „kein
 * is_admin-Toggle sichtbar"; Create-/Delete-User-Kontrollen analog dazu, nicht
 * separat im Brief benannt, aber derselbe Backend-Guard).
 */
export function canManageUserAccounts(user: PermissionUser): boolean {
  return user.is_admin;
}

/**
 * Darf der „Rolle zuweisen"-Bereich (Dropdowns + „+"-Button) für DIESE Zeile angezeigt
 * werden (Final-Review I1)? Ein KuSe-Admin ohne `is_admin` könnte sich sonst z. B.
 * `bereichsleiter` im eigenen Segment selbst vergeben (Selbst-Eskalation) — das Backend
 * lehnt das seit I1 mit 403 ab (`role-admin.ts`), hier zusätzlich ausgeblendet, statt einen
 * Klick anzubieten, der serverseitig sicher scheitert. `is_admin` bleibt unberührt: Selbst-
 * vergabe ist dort harmlos (hat ohnehin alle Rechte), siehe `canAssignFunctionRole`.
 */
export function canOfferRoleAssignment(actor: PermissionUser, isSelfRow: boolean): boolean {
  return !(isSelfRow && isKuseAdminOnly(actor));
}
